import { stateful, useState, useObservable } from '@adornis/functional-lit/stateful.js';
import { importPartnerNetworkWorkbook } from '@nsc/workflows-partner-scheduling/api/import-partner-network-workbook.js';
import { subscribeImportStatus } from '@nsc/workflows-partner-scheduling/api/subscribe-import-status.js';
import { html, nothing } from 'lit';
import { PartnerSchedulingImportRunStatus } from '@nsc/workflows-partner-scheduling/entities/PartnerSchedulingImportRun.js';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export const PartnerSchedulingImportPage = stateful(() => {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const importStatus = useObservable(() => subscribeImportStatus()(), []);
  const [status, ...messages] = importStatus ?? [PartnerSchedulingImportRunStatus.SUCCESS];

  const isInitializing = importStatus === undefined;

  // File selection
  const handleFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    setError(null);
    if (!file) {
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
  };

  // Import button
  const handleImport = async () => {
    if (!pendingFile) {
      setError('Choose a workbook file first.');
      return;
    }
    setError(null);
    setIsRequesting(true);
    try {
      const base64Content = await fileToBase64(pendingFile);
      // Fire the import; updates will come through the subscription
      await importPartnerNetworkWorkbook(base64Content, pendingFile.name)();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsRequesting(false);
    }
  };

  const goClients = () => {
    globalThis.location.assign('/partner-scheduling/clients');
  };

  const isRunning = status === PartnerSchedulingImportRunStatus.RUNNING;
  // Whether ANY async activity is blocking the UI
  const isBusy = isInitializing || isRequesting || isRunning;

  return html`
    <div class="min-h-screen bg-base-100 font-sans p-6">
      <div class="max-w-3xl mx-auto flex flex-col gap-6">
        <div class="flex items-center gap-4">
          <button type="button" class="btn btn-ghost btn-sm" @click=${goClients}>← Back</button>
          <h1 class="text-2xl font-extrabold text-neutral">Import partner workbook</h1>
        </div>

        <section class="card bg-base-100 shadow-xl rounded-2xl border border-neutral/10">
          <div class="card-body gap-4">
            <p class="text-base text-base-content/70">
              Upload <span class="font-semibold">Global Partner Network Database</span> (.xlsb). This replaces the
              existing partner network data. Processing runs on the server after you click Import.
            </p>

            ${isRunning && !pendingFile
              ? html`
                  <div class="alert alert-warning text-sm flex items-center gap-2">
                    <span class="loading loading-spinner loading-sm"></span>
                    <span>An import is currently in progress. Waiting for it to finish…</span>
                  </div>
                `
              : nothing}

            <div class="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div class="form-control w-full max-w-md">
                <label class="label py-1">
                  <span class="label-text font-medium">Workbook file</span>
                </label>
                <input
                  type="file"
                  ?disabled=${isBusy}
                  class="file-input file-input-bordered w-full"
                  accept=".xlsb,application/vnd.ms-excel.sheet.binary.macroEnabled.12"
                  @change=${handleFileChange}
                />
              </div>
              <button type="button" class="btn btn-primary" ?disabled=${isBusy || !pendingFile} @click=${handleImport}>
                ${isRunning || isRequesting
                  ? html`<span class="loading loading-spinner loading-sm"></span> Processing import…`
                    : 'Import'}
              </button>
            </div>

            ${pendingFile
              ? html`<p class="text-sm text-base-content/60">
                  Selected: <span class="font-mono">${pendingFile.name}</span>
                </p>`
              : nothing}
            ${error ? html`<div role="alert" class="alert alert-error text-sm"><span>${error}</span></div>` : nothing}
            ${messages.length > 0
              ? html`
                  <div class="rounded-lg border border-info/30 bg-info/5 p-4">
                    <h2 class="font-bold text-info mb-2">Messages</h2>
                    <ul class="list-disc list-inside space-y-1 text-sm font-mono whitespace-pre-wrap wrap-break-word">
                      ${messages.map(m => html`<li class="list-item">${m}</li>`)}
                    </ul>
                  </div>
                `
              : nothing}
          </div>
        </section>
      </div>
    </div>
  `;
});
